import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Badge, Button, Input, Modal, ModalBody, ModalFooter, ModalHeader, Spinner } from 'reactstrap';
import cs_analysis_fetch, { cs_analysis_raw } from '../../../utils/cs_analysis_fetch';
import Thread from './CommsThread';
import NotifyToggle, { notify_mode } from './NotifyToggle';
import TicketTasksCard from './TicketTasksCard';
import { linked_actionables_of, ticket_store_id } from './task_ticket_links';
import { PRIVATE_LOCK_TITLE, is_raiser_private, source_chat_href, visibility_label } from './ticket_visibility';
import TriageStrip from './TriageStrip';
import {
    client_for_store, is_merged, is_pending_triage, merged_into_href, merged_into_label, parse_cap_input, ticket_cap_label,
    ticket_cap_of, ticket_cap_path, triage_label,
} from './ticket_triage';
import { can_unlink, fetch_org_members, mask_contact, member_link_chip, unlink_member } from './member_links';
import { bridged_group_name, channel_chat_href } from './bridge_labels';
import { UPLOAD_EXPIRED_WORDS, UPLOAD_LIMIT_WORDS, is_upload_expired, is_upload_limit, upload_error, upload_failure_words } from './media_refusals';
import { HELD_WORDS, hold_chip, offer_held_files } from './held_files';
import { HeldFilesList, UploadLimitLine } from './HeldFiles';
import FaIcon from '../../../components/FaIcon';

/**
 * "Client Comms" StoreDashboard tab — the CSP side of the client communication
 * platform (cs-analysis docs/design-client-comms.md). Two panes:
 *   left  — the org's general channel (client-visible; mirrors to the org's
 *           WhatsApp group once the Phase-2 bridge is enabled for the org)
 *   right — client tickets: list + create + detail drawer (status machine,
 *           per-ticket thread with internal notes, product-ticket links,
 *           audit history)
 * Staff-side API (store-keyed): /api/v1/comms/* + /api/v1/tickets/*.
 * Lifecycle milestones (created/resolved/…) appear in the org channel as
 * system messages — posted by the backend, never composed here.
 * 2026-09-13: client-raised tickets arrive PENDING triage (accept / merge /
 * decline in the drawer), the member screen shows how each WhatsApp number
 * was linked (self-verified vs rep-mapped) with Unlink, and the settings
 * column carries the org's open-ticket cap.
 */

const MODULES = ['DESIGN', 'RENDER', 'PRESENTATION', 'CATALOGUE', 'PRICING', 'PRODUCTION', 'MES', 'OTHER'];
const CATEGORIES = ['BUG', 'FEATURE_REQUEST', 'TRAINING', 'CATALOG_CONFIG', 'QUESTION'];
const PRIORITIES = ['LOW', 'NORMAL', 'HIGH', 'URGENT'];
const RESOLUTIONS = ['FIXED', 'WORKAROUND', 'ANSWERED', 'TRAINED', 'NOT_POSSIBLE', 'NOT_REQUIRED', 'DUPLICATE'];
const STATUS_COLORS = {
    OPEN: '#e67e22', IN_PROGRESS: '#2f6fed', WAITING_ON_CLIENT: '#8e44ad',
    WAITING_ON_PRODUCT: '#16a085', RESOLVED: '#27ae60', CLOSED: '#7f8c8d', DECLINED: '#c0392b',
};
// Title Case for enum display everywhere (Nikhil 2026-07-14: no lowercase labels).
const label = (s) => (s || '').split('_').map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1).toLowerCase() : '')).join(' ');
const title_case = (s) => label(s).replace(/(^|\s)\S/g, (c) => c.toUpperCase());
const fmt_ts = (d) => { try { return new Date(d).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }); } catch { return ''; } };


// ── Ticket detail workspace ─────────────────────────────────────────────────
// Rich two-pane view shared by the Client Tickets queue page and the org tab:
// left = every ticket affordance (inline field edits, assignment, status
// machine, product-ticket links, audit history); right = the conversation.
// Unlike product tickets — where the primary discussion happens in a GChat
// thread — the client-ticket conversation is primary HERE; GChat only gets
// threaded notifications pointing back to this page.
const DW = {
    body: { display: 'flex', gap: 14, height: '78vh', overflow: 'hidden', padding: 14, background: '#f6f8fa' },
    card: { background: '#fff', border: '1px solid #e5e9ee', borderRadius: 10, padding: '12px 14px', marginBottom: 12 },
    section: { fontSize: 11, fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase', color: '#8a97a5', marginBottom: 8 },
    label: { width: 106, minWidth: 106, fontSize: 12, color: '#8a97a5' },
    select: { fontSize: 12.5, height: 30, padding: '3px 8px', borderRadius: 6, border: '1px solid #d8dfe6', maxWidth: 230 },
    tbtn: { fontSize: 12, padding: '4px 13px', borderRadius: 15, border: '1px solid #c9d4de', background: '#fff', color: '#3d4f5f', cursor: 'pointer', lineHeight: '18px' },
};

// store_id (optional): the client's store id, for the Tasks card (the
// actionables API is store-keyed). The org tab passes its own; the queue relies
// on the list row's client.storeId (ticket_store_id).
export function TicketDrawer({ ticket, channel_id, on_close, on_changed, store_id }) {
    const [detail, set_detail] = useState(null); // fresh {ticket, channelId} from GET /:id
    const [history, set_history] = useState(null);
    const [product_tickets, set_product_tickets] = useState(null);
    const [reps, set_reps] = useState(null);
    const [pt_query, set_pt_query] = useState('');
    const [pt_results, set_pt_results] = useState([]);
    const [busy, set_busy] = useState(false);
    const [editing, set_editing] = useState(null); // {field: 'title'|'description', value}

    const refresh = useCallback(() => {
        if (!ticket) return;
        cs_analysis_fetch({ path: `/api/v1/tickets/${ticket.id}` }).then((r) => set_detail(r || null)).catch(() => {});
        cs_analysis_fetch({ path: `/api/v1/tickets/${ticket.id}/history` }).then((r) => set_history(r.history || [])).catch(() => set_history([]));
        cs_analysis_fetch({ path: `/api/v1/tickets/${ticket.id}/product-tickets` }).then((r) => set_product_tickets(r.productTickets || [])).catch(() => set_product_tickets([]));
    }, [ticket && ticket.id]);

    useEffect(() => {
        if (!ticket) return;
        set_detail(null); set_history(null); set_product_tickets(null);
        set_pt_results([]); set_pt_query(''); set_editing(null);
        refresh();
    }, [ticket && ticket.id, refresh]);

    useEffect(() => {
        cs_analysis_fetch({ path: '/api/v1/cs/reps' }).then((r) => set_reps(r)).catch(() => set_reps({ reps: [], self: null }));
    }, []);
    // Who am I, for the thread: ↑-recall of my own messages (team request via
    // Nikhil, 2026-09-15) and the ✎/🗑 affordances on them key off me_id, which
    // this drawer never passed. Same source as the Chat inbox (/auth/me →
    // user.id — the id STAFF posts carry as authorId).
    const [me_id, set_me_id] = useState(null);
    useEffect(() => {
        cs_analysis_fetch({ path: '/api/v1/auth/me' }).then((r) => set_me_id(r && r.user && r.user.id)).catch(() => {});
    }, []);

    if (!ticket) return null;
    const t = (detail && detail.ticket) || ticket;
    const chan = channel_id || (detail && detail.channelId) || null;

    const act = async (fn) => {
        if (busy) return;
        set_busy(true);
        try { await fn(); await on_changed(); refresh(); }
        catch (e) { window.alert((e && e.message) || 'action failed'); }
        set_busy(false);
    };
    const patch_fields = (fields) => act(() => cs_analysis_fetch({
        path: `/api/v1/tickets/${t.id}`, method: 'PATCH',
        body: { ...fields, expectedVersion: t.version },
    }));
    const assign = (fields) => act(() => cs_analysis_fetch({
        path: `/api/v1/tickets/${t.id}/assign`, method: 'POST',
        body: { ...fields, expectedVersion: t.version },
    }));
    const set_status = (status, extra) => act(() => cs_analysis_fetch({
        path: `/api/v1/tickets/${t.id}/status`, method: 'POST',
        body: { status, expectedVersion: t.version, ...extra },
    }));
    const save_edit = () => {
        if (!editing) return;
        const v = (editing.value || '').trim();
        if (v && v !== t[editing.field]) patch_fields({ [editing.field]: v });
        set_editing(null);
    };
    const resolve = () => {
        const idx = window.prompt('Resolution:\n' + RESOLUTIONS.map((r, i) => `${i + 1}. ${label(r)}`).join('\n') + '\n\nEnter number:');
        const resolution = RESOLUTIONS[Number(idx) - 1];
        if (!resolution) return;
        set_status('RESOLVED', { resolution });
    };
    const decline = () => {
        const reason = window.prompt('Decline reason (CLIENT-SAFE wording — the client sees this):');
        if (!reason || !reason.trim()) return;
        set_status('DECLINED', { declineReason: reason.trim() });
    };
    const search_pt = async () => {
        if (!pt_query.trim()) return;
        const r = await cs_analysis_fetch({ path: '/api/v1/tickets/search-product-tickets', query: { q: pt_query.trim() } });
        set_pt_results(Array.isArray(r) ? r : []);
    };
    const link_pt = (pid) => act(() => cs_analysis_fetch({
        path: `/api/v1/tickets/${t.id}/product-tickets`, method: 'POST', body: { productTicketId: pid },
    }));
    const unlink_pt = (pid) => act(() => cs_analysis_fetch({
        path: `/api/v1/tickets/${t.id}/product-tickets/${encodeURIComponent(pid)}`, method: 'DELETE',
    }));

    const s = t.status;
    const open_ish = ['OPEN', 'IN_PROGRESS', 'WAITING_ON_CLIENT', 'WAITING_ON_PRODUCT'].includes(s);
    const rep_list = (reps && reps.reps) || [];
    const row = (k, v) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '4px 0' }}>
            <div style={DW.label}>{k}</div>
            <div style={{ flex: 1, minWidth: 0, fontSize: 12.5, color: '#33414e' }}>{v}</div>
        </div>
    );
    const dot_color = (kind) => (
        kind === 'created' ? '#2f6fed'
            : String(kind).indexOf('status') === 0 ? '#8e44ad'
                : kind === 'assigned' ? '#16a085'
                    : kind === 'reopened' ? '#e67e22' : '#b9c4cd'
    );

    return (
        <Modal isOpen toggle={on_close} size='xl' style={{ maxWidth: 'min(1320px, 95vw)' }}>
            <ModalHeader toggle={on_close} style={{ padding: '13px 18px' }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: '#64748b', background: '#eef2f6', borderRadius: 6, padding: '2px 8px', marginRight: 10, verticalAlign: 'middle' }}>CT-{t.ref}</span>
                {is_raiser_private(t) ? (
                    <span title={PRIVATE_LOCK_TITLE} style={{ fontSize: 11, fontWeight: 600, color: '#8a6d1d', background: '#fff8e6', border: '1px solid #f0d48a', borderRadius: 10, padding: '1px 8px', marginRight: 10, verticalAlign: 'middle', whiteSpace: 'nowrap' }}>
                        <FaIcon name='lock' label='private to raiser' /> Private to raiser
                    </span>
                ) : null}
                {editing && editing.field === 'title' ? (
                    <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                        <Input bsSize='sm' value={editing.value} autoFocus
                            onChange={(e) => set_editing({ field: 'title', value: e.target.value })}
                            onKeyDown={(e) => { if (e.key === 'Enter') save_edit(); if (e.key === 'Escape') set_editing(null); }}
                            style={{ width: 'min(480px, 52vw)', display: 'inline-block' }} />
                        <Button size='sm' color='primary' disabled={busy} onClick={save_edit}>Save</Button>
                    </span>
                ) : (
                    <span style={{ fontSize: 15.5, fontWeight: 600, color: '#233240', whiteSpace: 'normal', wordBreak: 'break-word' }}>
                        {t.title}
                        <Button size='sm' color='link' title='edit title' aria-label='edit title' style={{ padding: '0 6px', fontSize: 12 }}
                            onClick={() => set_editing({ field: 'title', value: t.title })}><FaIcon name='pen' /></Button>
                    </span>
                )}
                <Badge style={{ backgroundColor: STATUS_COLORS[s] || '#95a5a6', color: '#fff', marginLeft: 8, padding: '4px 10px', borderRadius: 11, fontSize: 11, verticalAlign: 'middle' }}>{label(s)}</Badge>
                {detail ? (
                    <button type='button' disabled={busy}
                        title={detail.following ? 'Stop getting updates for this ticket' : 'Get updates for this ticket (unread + push)'}
                        onClick={() => act(() => cs_analysis_fetch({ path: `/api/v1/tickets/${t.id}/follow`, method: detail.following ? 'DELETE' : 'POST' }))}
                        style={{ ...DW.tbtn, marginLeft: 10, padding: '2px 12px', verticalAlign: 'middle', border: '1px solid ' + (detail.following ? '#2f6fed' : '#c9d4de'), color: detail.following ? '#2f6fed' : '#3d4f5f' }}>
                        <FaIcon name='star' regular={!detail.following} style={{ marginRight: 4 }} />{detail.following ? 'Following' : 'Follow'}
                    </button>
                ) : null}
            </ModalHeader>
            <ModalBody style={DW.body}>
                {/* left — ticket affordances */}
                <div style={{ width: 404, minWidth: 340, overflowY: 'auto', paddingRight: 2 }}>
                    {is_pending_triage(t) ? (
                        <div style={{ ...DW.card, background: '#fffdf5', border: '1px solid #f0d48a' }}>
                            <div style={DW.section}>Triage</div>
                            <div style={{ fontSize: 12, color: '#6b5a1e', marginBottom: 8 }}>
                                Raised by the client and assigned to you. Accept it, merge it into an existing ticket of this org, or decline it with a reason the client will read.
                            </div>
                            <TriageStrip ticket={t} store_id={ticket_store_id(t, store_id) || ticket_store_id(ticket)}
                                on_changed={async () => { await on_changed(); refresh(); }} />
                        </div>
                    ) : null}
                    {is_merged(t) ? (
                        <div style={{ ...DW.card, background: '#f7f4ff', border: '1px solid #ddd2f6', fontSize: 12.5, color: '#5b3fa0' }}>
                            <FaIcon name='arrow-right' /> <b>{merged_into_label(t)}</b>
                            {merged_into_href(t) ? (
                                <a href={merged_into_href(t)} target='_blank' rel='noreferrer noopener' style={{ marginLeft: 8 }} title='Open the ticket this one was merged into'>open <FaIcon name='external-link-alt' /></a>
                            ) : null}
                            <div style={{ fontSize: 11, color: '#8a97a5', marginTop: 2 }}>Closed as a duplicate; its raiser was pointed to the target ticket.</div>
                        </div>
                    ) : null}
                    <div style={DW.card}>
                        <div style={DW.section}>
                            Description
                            {editing && editing.field === 'description' ? null : (
                                <Button size='sm' color='link' title='edit description' aria-label='edit description' style={{ padding: '0 6px', fontSize: 12, textTransform: 'none' }}
                                    onClick={() => set_editing({ field: 'description', value: t.description })}><FaIcon name='pen' /></Button>
                            )}
                        </div>
                        {editing && editing.field === 'description' ? (
                            <>
                                <Input type='textarea' rows={5} value={editing.value} autoFocus
                                    onChange={(e) => set_editing({ field: 'description', value: e.target.value })} style={{ fontSize: 13 }} />
                                <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                                    <Button size='sm' color='primary' disabled={busy} onClick={save_edit}>Save</Button>
                                    <Button size='sm' color='link' onClick={() => set_editing(null)}>cancel</Button>
                                </div>
                            </>
                        ) : (
                            <div style={{ whiteSpace: 'pre-wrap', fontSize: 13, lineHeight: 1.5, color: '#33414e', maxHeight: 170, overflowY: 'auto' }}>{t.description}</div>
                        )}
                    </div>

                    <div style={DW.card}>
                        <div style={DW.section}>Details</div>
                        {row('Priority', (
                            <Input type='select' bsSize='sm' value={t.priority} disabled={busy} style={DW.select}
                                onChange={(e) => patch_fields({ priority: e.target.value })}>
                                {PRIORITIES.map((p) => <option key={p} value={p}>{label(p)}</option>)}
                            </Input>
                        ))}
                        {row('Module', (
                            <Input type='select' bsSize='sm' value={t.module} disabled={busy} style={DW.select}
                                onChange={(e) => patch_fields({ module: e.target.value })}>
                                {MODULES.map((m) => <option key={m} value={m}>{label(m)}</option>)}
                            </Input>
                        ))}
                        {row('Category', (
                            <Input type='select' bsSize='sm' value={t.category} disabled={busy} style={DW.select}
                                onChange={(e) => patch_fields({ category: e.target.value })}>
                                {CATEGORIES.map((c) => <option key={c} value={c}>{label(c)}</option>)}
                            </Input>
                        ))}
                        {row('Raised by', <><b>{t.raisedByName || t.raisedById}</b>&nbsp;<span style={{ color: '#8a97a5' }}>({t.raisedByType === 'CLIENT_USER' ? 'client' : 'Infurnia'}) · {fmt_ts(t.raisedAt)}</span></>)}
                        {triage_label(t) && !is_pending_triage(t) ? row('Triage', (
                            <span>
                                {triage_label(t)}
                                {t.acceptedAt ? (
                                    <span style={{ color: '#8a97a5' }}>
                                        {' · '}{fmt_ts(t.acceptedAt)}
                                        {t.acceptedBy ? ` by ${(rep_list.find((r) => r.userId === t.acceptedBy) || {}).name || 'Infurnia'}` : ''}
                                    </span>
                                ) : null}
                            </span>
                        )) : null}
                        {/* Unified client chat: tickets raised from a user's private portal chat
                            are RAISER-visible and carry the chat they came from. */}
                        {visibility_label(t) ? row('Visibility', (
                            is_raiser_private(t)
                                ? <span title={PRIVATE_LOCK_TITLE}><FaIcon name='lock' label='private to raiser' /> Private to raiser <span style={{ color: '#8a97a5' }}>· the org does not see it</span></span>
                                : <span>{visibility_label(t)}</span>
                        )) : null}
                        {source_chat_href(t) ? row('Raised from', (
                            <a href={source_chat_href(t)} target='_blank' rel='noreferrer noopener' title='Open the chat this ticket was raised from'>
                                <FaIcon name='comment' label='chat' /> chat with {t.raisedByType === 'CLIENT_USER' && t.raisedByName ? t.raisedByName : 'the client'} <FaIcon name='external-link-alt' />
                            </a>
                        )) : null}
                        {t.resolution ? row('Resolution', <b>{label(t.resolution)}</b>) : null}
                        {t.declineReason ? row('Declined', <i>{t.declineReason}</i>) : null}
                        {t.resolvedAt ? row('Resolved at', fmt_ts(t.resolvedAt)) : null}
                        {t.closedAt ? row('Closed', `${fmt_ts(t.closedAt)}${t.closeKind === 'AUTO' ? ' (auto)' : t.closeKind ? ' (client-verified)' : ''}`) : null}
                    </div>

                    <div style={DW.card}>
                        <div style={DW.section}>Assignment</div>
                        {row('Infurnia owner', (
                            <Input type='select' bsSize='sm' value={t.assigneeStaffId || ''} disabled={busy || !reps} style={DW.select}
                                onChange={(e) => assign({ assigneeStaffId: e.target.value || null })}>
                                <option value=''>— unassigned —</option>
                                {rep_list.map((r) => <option key={r.userId} value={r.userId}>{r.name}</option>)}
                                {t.assigneeStaffId && !rep_list.some((r) => r.userId === t.assigneeStaffId)
                                    ? <option value={t.assigneeStaffId}>{t.assigneeStaffId}</option> : null}
                            </Input>
                        ))}
                        {row('Owner side', (
                            <div style={{ display: 'flex', gap: 5 }}>
                                {['INFURNIA', 'CLIENT'].map((side) => (
                                    <button key={side} type='button' disabled={busy}
                                        style={{
                                            ...DW.tbtn, padding: '2px 12px',
                                            border: '1px solid ' + (t.ownerSide === side ? '#2f6fed' : '#c9d4de'),
                                            background: t.ownerSide === side ? '#2f6fed' : '#fff',
                                            color: t.ownerSide === side ? '#fff' : '#3d4f5f',
                                        }}
                                        onClick={() => { if (t.ownerSide !== side) assign({ ownerSide: side }); }}>
                                        {title_case(side)}
                                    </button>
                                ))}
                            </div>
                        ))}
                        {t.assigneeClientUserId ? row('Client owner', t.assigneeClientUserId) : null}
                    </div>

                    <div style={DW.card}>
                        <div style={DW.section}>Move it forward</div>
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                            {s === 'OPEN' ? <button type='button' style={{ ...DW.tbtn, border: '1px solid #2f6fed', color: '#2f6fed' }} disabled={busy} onClick={() => set_status('IN_PROGRESS')}><FaIcon name='play' /> Start</button> : null}
                            {open_ish && s !== 'IN_PROGRESS' && s !== 'OPEN' ? <button type='button' style={DW.tbtn} disabled={busy} onClick={() => set_status('IN_PROGRESS')}>Back in progress</button> : null}
                            {open_ish && s !== 'WAITING_ON_CLIENT' ? <button type='button' style={DW.tbtn} disabled={busy} onClick={() => set_status('WAITING_ON_CLIENT')}>Waiting on client</button> : null}
                            {open_ish && s !== 'WAITING_ON_PRODUCT' ? <button type='button' style={DW.tbtn} disabled={busy} onClick={() => set_status('WAITING_ON_PRODUCT')}>With product team</button> : null}
                        </div>
                        {open_ish || s === 'RESOLVED' || s === 'CLOSED' || s === 'DECLINED' ? (
                            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8, paddingTop: 8, borderTop: '1px dashed #e5e9ee' }}>
                                {open_ish ? <button type='button' style={{ ...DW.tbtn, background: '#27ae60', border: '1px solid #27ae60', color: '#fff' }} disabled={busy} onClick={resolve}><FaIcon name='check' /> Resolve…</button> : null}
                                {open_ish ? <button type='button' style={{ ...DW.tbtn, border: '1px solid #e08283', color: '#c0392b' }} disabled={busy} onClick={decline}>Decline…</button> : null}
                                {open_ish && t.priority !== 'URGENT' ? (
                                    <button type='button' style={{ ...DW.tbtn, border: '1px solid #e6b35a', color: '#b9770e' }} disabled={busy}
                                        onClick={() => {
                                            const note = window.prompt('Escalation note (goes to the team GChat thread):');
                                            if (note === null) return;
                                            act(() => cs_analysis_fetch({ path: `/api/v1/tickets/${t.id}/escalate`, method: 'POST', body: note.trim() ? { note: note.trim() } : {} }));
                                        }}>
                                        <FaIcon name='exclamation-circle' /> Escalate
                                    </button>
                                ) : null}
                                {s === 'RESOLVED' ? <button type='button' style={{ ...DW.tbtn, background: '#27ae60', border: '1px solid #27ae60', color: '#fff' }} disabled={busy} onClick={() => set_status('CLOSED')}>Close (client confirmed verbally)</button> : null}
                                {s === 'CLOSED' || s === 'DECLINED' ? <button type='button' style={{ ...DW.tbtn, border: '1px solid #e6b35a', color: '#b9770e' }} disabled={busy} onClick={() => act(() => cs_analysis_fetch({ path: `/api/v1/tickets/${t.id}/reopen`, method: 'POST' }))}>Reopen</button> : null}
                            </div>
                        ) : null}
                        {s === 'RESOLVED' ? <div style={{ fontSize: 11.5, color: '#8a97a5', marginTop: 6 }}>auto-closes after 7 days without client verification</div> : null}
                    </div>

                    <div style={DW.card}>
                        <div style={DW.section}>Product tickets</div>
                        {product_tickets === null ? <Spinner size='sm' /> : product_tickets.length === 0 ? (
                            <div style={{ fontSize: 12, color: '#a4b0bb', marginBottom: 4 }}>None linked yet — link the engineering ticket this depends on.</div>
                        ) : product_tickets.map((p) => (
                            <div key={p.id} style={{ fontSize: 12.5, padding: '3px 0', display: 'flex', alignItems: 'center', gap: 6 }}>
                                <Badge color='secondary' style={{ fontSize: 10 }}>{p.status}</Badge>
                                <a href={`/home/customer_tickets?open_ticket=${encodeURIComponent(p.id)}`} target='_blank' rel='noreferrer noopener'
                                    style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.title || p.id} <FaIcon name='external-link-alt' /></a>
                                <Button size='sm' color='link' title='unlink' aria-label='unlink' disabled={busy} style={{ padding: 0, fontSize: 12, color: '#c0392b' }}
                                    onClick={() => { if (window.confirm('Unlink this product ticket?')) unlink_pt(p.id); }}><FaIcon name='times' /></Button>
                            </div>
                        ))}
                        <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                            <Input bsSize='sm' placeholder='Search product tickets to link…' value={pt_query}
                                onChange={(e) => set_pt_query(e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter') search_pt(); }}
                                style={{ fontSize: 12.5, height: 30, borderRadius: 6, border: '1px solid #d8dfe6' }} />
                            <Button size='sm' outline onClick={search_pt} style={{ borderRadius: 6 }}>Search</Button>
                        </div>
                        {pt_results.map((p) => (
                            <div key={p.id} style={{ fontSize: 12, padding: '3px 0' }}>
                                <Button size='sm' color='link' style={{ padding: 0, fontSize: 12 }} disabled={busy} onClick={() => link_pt(p.id)}>+ link</Button>{' '}
                                <span style={{ color: '#8a97a5' }}>[{p.status}]</span> {p.title || p.id}
                            </div>
                        ))}
                    </div>

                    {/* Tasks ↔ this ticket (refinement A): linked tasks, create-from-ticket,
                        link-existing. The detail row (GET /:id) has no client row, so the
                        store id falls back to the list row the drawer was opened with. */}
                    <TicketTasksCard
                        ticket={t}
                        store_id={ticket_store_id(t, store_id) || ticket_store_id(ticket)}
                        linked={linked_actionables_of(detail)}
                        reps_info={reps}
                        busy={busy}
                        on_changed={async () => { await on_changed(); refresh(); }}
                        styles={DW}
                    />

                    <div style={{ ...DW.card, marginBottom: 2 }}>
                        <div style={DW.section}>History</div>
                        {history === null ? <Spinner size='sm' /> : history.map((h) => (
                            <div key={h.id} style={{ display: 'flex', gap: 8, padding: '2px 0', fontSize: 11.5, color: '#7f8c8d' }}>
                                <span style={{ width: 7, height: 7, minWidth: 7, borderRadius: 4, marginTop: 5, background: dot_color(h.changeType) }} />
                                <span>
                                    {fmt_ts(h.changedAt)} · <b style={{ color: '#4a5866' }}>{h.changeType}</b>
                                    {h.byUserId ? ' · Infurnia' : h.byClientUserId ? ' · client' : ' · system'}
                                    {h.note ? ` — ${h.note}` : ''}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>

                {/* right — the conversation (primary surface for client tickets) */}
                <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', background: '#fff', border: '1px solid #e5e9ee', borderRadius: 10, padding: '10px 14px 12px' }}>
                    <div style={{ marginBottom: 8, borderBottom: '1px solid #f0f3f6', paddingBottom: 8 }}>
                        <span style={{ fontSize: 13, fontWeight: 700, color: '#233240' }}>Conversation</span>
                        <span style={{ fontSize: 11.5, color: '#a4b0bb', marginLeft: 10 }}>
                            primary thread — client-visible replies reach the client's support panel; tick "internal note" for team-only
                        </span>
                    </div>
                    <div style={{ flex: 1, minHeight: 0 }}>
                        {chan ? (
                            <Thread channel_id={chan} allow_internal height='100%' me_id={me_id}
                                placeholder='Reply (visible to client) — tick "internal note" for team-only' />
                        ) : <div style={{ fontSize: 12, color: '#a4b0bb' }}>no channel</div>}
                    </div>
                </div>
            </ModalBody>
        </Modal>
    );
}

// ── Create-ticket modal ─────────────────────────────────────────────────────
export function CreateTicketModal({ store_id, open, on_close, on_created, upload_channel_id, initial }) {
    const [form, set_form] = useState({ module: 'DESIGN', category: 'BUG', priority: 'NORMAL', title: '', description: '' });
    useEffect(() => {
        if (open && initial) set_form((f) => ({ ...f, ...initial }));
    }, [open, initial]);
    const [busy, set_busy] = useState(false);
    const [pending_media, set_pending_media] = useState(null);
    const [uploading, set_uploading] = useState(false);
    // The upload limit's line (CT-15747): null, or { see_files } once the api answered the list (held_files.js rule 2);
    // "See files" opens the list inside this modal (no modal over a modal).
    const [limit_line, set_limit_line] = useState(null);
    const [held_open, set_held_open] = useState(false);
    // The chip's file is "In this message" on the list, which then offers no Remove for it (held_files.js rule 3).
    const chip_id = pending_media ? pending_media.mediaId : null;
    useEffect(() => (chip_id ? hold_chip(chip_id) : undefined), [chip_id]);
    const file_ref = useRef(null);
    // react-dom 16 pools synthetic events: a functional updater runs AFTER the
    // handler returns, by which time e.target is null. Read the value first.
    const upd = (k) => (e) => { const v = e.target.value; set_form((f) => ({ ...f, [k]: v })); };
    const upload = async (file) => {
        if (!file || uploading || !upload_channel_id) return;
        if (file.size > 16 * 1024 * 1024) { window.alert('Attachments are capped at 16MB.'); return; }
        set_uploading(true);
        try {
            const fd = new FormData();
            fd.append('file', file);
            const res = await cs_analysis_raw({ path: `/api/v1/comms/channels/${upload_channel_id}/media`, method: 'POST', form: fd });
            if (!res.ok) throw await upload_error(res);
            set_pending_media(await res.json());
            set_limit_line(null);
            set_held_open(false);
        } catch (e) {
            if (is_upload_limit(e)) {
                if (await offer_held_files()) set_limit_line({ see_files: true });
                else window.alert(UPLOAD_LIMIT_WORDS);
            } else window.alert(upload_failure_words(e));
        }
        set_uploading(false);
        if (file_ref.current) file_ref.current.value = '';
    };
    const submit = async () => {
        if (!form.title.trim() || !form.description.trim() || busy) return;
        set_busy(true);
        try {
            await cs_analysis_fetch({
                path: '/api/v1/tickets', method: 'POST',
                body: { storeId: store_id, ...form, ...(pending_media ? { mediaIds: [pending_media.mediaId] } : {}) },
            });
            set_form({ module: 'DESIGN', category: 'BUG', priority: 'NORMAL', title: '', description: '' });
            set_pending_media(null);
            on_created();
        } catch (e) {
            if (is_upload_expired(e)) {
                set_pending_media(null);
                window.alert(UPLOAD_EXPIRED_WORDS);
            } else window.alert((e && e.message) || 'create failed');
        }
        set_busy(false);
    };
    return (
        <Modal isOpen={open} toggle={on_close}>
            <ModalHeader toggle={on_close}>New client ticket</ModalHeader>
            <ModalBody>
                <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                    <Input type='select' bsSize='sm' value={form.module} onChange={upd('module')}>
                        {MODULES.map((m) => <option key={m} value={m}>{label(m)}</option>)}
                    </Input>
                    <Input type='select' bsSize='sm' value={form.category} onChange={upd('category')}>
                        {CATEGORIES.map((c) => <option key={c} value={c}>{label(c)}</option>)}
                    </Input>
                    <Input type='select' bsSize='sm' value={form.priority} onChange={upd('priority')}>
                        {PRIORITIES.map((p) => <option key={p} value={p}>{label(p)}</option>)}
                    </Input>
                </div>
                <Input bsSize='sm' placeholder='Title' value={form.title} onChange={upd('title')} style={{ marginBottom: 8 }} />
                <Input type='textarea' rows={4} placeholder='Description (visible to the client)' value={form.description} onChange={upd('description')} style={{ fontSize: 13 }} />
                {pending_media ? (
                    <div style={{ fontSize: 11, color: '#7f8c8d', background: '#f6f8f9', borderRadius: 4, padding: '3px 8px', marginTop: 8 }}>
                        <FaIcon name='paperclip' /> {pending_media.filename}
                        <span onClick={() => set_pending_media(null)} title='Remove the attachment' aria-label='Remove the attachment' style={{ cursor: 'pointer', float: 'right', color: '#e74c3c' }}><FaIcon name='times' /></span>
                    </div>
                ) : null}
                {limit_line ? (
                    <UploadLimitLine see_files={limit_line.see_files} on_see_files={() => set_held_open((o) => !o)}
                        on_dismiss={() => { set_limit_line(null); set_held_open(false); }} />
                ) : null}
                {limit_line && held_open ? (
                    <div style={{ marginTop: 8, border: '1px solid #ecf0f1', borderRadius: 8, padding: '6px 12px' }}>
                        <div style={{ fontWeight: 600, fontSize: 13, paddingTop: 4 }}>{HELD_WORDS.title}</div>
                        <HeldFilesList on_no_route={() => set_limit_line((l) => (l ? { see_files: false } : l))} />
                    </div>
                ) : null}
            </ModalBody>
            <ModalFooter>
                {upload_channel_id ? (
                    <>
                        <input ref={file_ref} type='file' style={{ display: 'none' }} onChange={(e) => upload(e.target.files && e.target.files[0])} />
                        <Button size='sm' color='light' disabled={uploading || !!pending_media} title='Attach a screenshot / file'
                            onClick={() => file_ref.current && file_ref.current.click()}>{uploading ? '…' : <><FaIcon name='paperclip' /> Attach</>}</Button>
                    </>
                ) : null}
                <Button size='sm' color='primary' disabled={busy || !form.title.trim() || !form.description.trim()} onClick={submit}>Create</Button>
                <Button size='sm' color='secondary' outline onClick={on_close}>Cancel</Button>
            </ModalFooter>
        </Modal>
    );
}

// ── Per-group WhatsApp bridge management (design §5; per-group switch) ──────
function BridgePanel({ store_id, groups, candidates, on_changed }) {
    const [open, set_open] = useState(false);
    const [picks, set_picks] = useState({}); // channelId -> waGroupId
    const [busy, set_busy] = useState(false);
    const act = async (fn) => {
        if (busy) return; set_busy(true);
        try { await fn(); await on_changed(); } catch (e) { window.alert((e && e.message) || 'failed'); }
        set_busy(false);
    };
    return (
        <div style={{ marginTop: 10 }}>
            <div style={{ fontSize: 12, fontWeight: 600, cursor: 'pointer', color: '#2f6fed' }} onClick={() => set_open((v) => !v)}>
                <FaIcon name={open ? 'caret-down' : 'caret-right'} /> WhatsApp bridge
            </div>
            {open ? (
                <div style={{ fontSize: 12, marginTop: 6 }}>
                    {(groups || []).map((g) => (
                        <div key={g.id} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '3px 0', borderBottom: '1px solid #f0f2f4' }}>
                            <span style={{ flex: '0 0 110px', fontWeight: 600 }}>{title_case(g.groupKey)}</span>
                            {g.waGroupId ? (
                                <>
                                    <Badge color='success'>bridged</Badge>
                                    <span title={g.waGroupId} style={{ flex: 1, color: '#34495e', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {bridged_group_name(g, candidates)}
                                    </span>
                                    <a href={channel_chat_href(g)} title='Open this group in the Chat inbox' style={{ fontSize: 11, whiteSpace: 'nowrap' }}>open chat →</a>
                                    <Button size='sm' color='link' style={{ padding: 0, fontSize: 11, color: '#c0392b' }} disabled={busy}
                                        onClick={() => act(() => cs_analysis_fetch({ path: `/api/v1/comms/channels/${g.id}/bridge`, method: 'DELETE' }))}>
                                        disable
                                    </Button>
                                </>
                            ) : (
                                <>
                                    <Input type='select' bsSize='sm' style={{ flex: 1 }} value={picks[g.id] || ''}
                                        onChange={(e) => { const v = e.target.value; set_picks((pp) => ({ ...pp, [g.id]: v })); }}>
                                        <option value=''>— WhatsApp group —</option>
                                        {(candidates || []).filter((c) => !c.bridged).map((c) => (
                                            <option key={c.groupId} value={c.groupId}>{c.groupName} ({(c.groupType || '').toLowerCase()})</option>
                                        ))}
                                    </Input>
                                    <Button size='sm' color='primary' outline style={{ fontSize: 11, padding: '1px 8px' }}
                                        disabled={busy || !picks[g.id]}
                                        onClick={() => act(() => cs_analysis_fetch({ path: `/api/v1/comms/channels/${g.id}/bridge`, method: 'POST', body: { waGroupId: picks[g.id] } }))}>
                                        enable
                                    </Button>
                                </>
                            )}
                        </div>
                    ))}
                    <div style={{ color: '#95a5a6', marginTop: 4 }}>
                        Bridged groups mirror both ways. Outbound additionally needs the group on the posting allow-list (admin/env).
                    </div>
                </div>
            ) : null}
        </div>
    );
}

// ── WhatsApp members (bridge identity mapping — design §3.2) ────────────────
// Each row shows HOW the number is tied to a portal user: "Verified" when the
// user linked it themselves from the portal, "Mapped by rep" when a rep did
// it here; Unlink drops either back to guest (member_links.js).
export function WaMembers({ store_id, default_open }) {
    const [open, set_open] = useState(!!default_open);
    const [data, set_data] = useState(null);
    const [picks, set_picks] = useState({}); // memberId -> orgUserId | 'rep:<userId>'
    const [busy, set_busy] = useState(false);

    const load = useCallback(async () => {
        try { set_data(await fetch_org_members(cs_analysis_fetch, store_id)); }
        catch (e) { set_data({ members: [], orgUsers: [], error: (e && e.message) || 'unavailable' }); }
    }, [store_id]);
    useEffect(() => { if (open) load(); }, [open, load]);

    const act = async (fn) => {
        if (busy) return; set_busy(true);
        try { await fn(); await load(); } catch (e) { window.alert((e && e.message) || 'failed'); }
        set_busy(false);
    };
    const map_member = (m) => act(() => {
        const pick = picks[m.id] || '';
        if (pick.indexOf('rep:') === 0) {
            return cs_analysis_fetch({
                path: `/api/v1/comms/clients/${store_id}/wa-members/${m.id}/map-rep`, method: 'POST',
                body: { userId: pick.slice(4) },
            });
        }
        return cs_analysis_fetch({
            path: `/api/v1/comms/clients/${store_id}/wa-members/${m.id}/map`, method: 'POST',
            body: { mappedUserId: pick },
        });
    });
    const unmap_member = (m) => act(() => cs_analysis_fetch({
        path: `/api/v1/comms/clients/${store_id}/wa-members/${m.id}/map`, method: 'DELETE',
    }));
    const unlink = (m) => {
        if (!window.confirm('Unlink this WhatsApp number from the user? Their messages go back to being attributed to the number.')) return;
        act(() => unlink_member(cs_analysis_fetch, store_id, m.id));
    };

    return (
        <div style={{ marginTop: default_open ? 0 : 12 }}>
            <div style={{ fontSize: 12, fontWeight: 600, cursor: 'pointer', color: '#2f6fed' }}
                onClick={() => set_open((v) => !v)}>
                <FaIcon name={open ? 'caret-down' : 'caret-right'} /> WhatsApp members {data && data.members ? `(${data.members.length})` : ''}
            </div>
            {open ? (
                data === null ? <Spinner size='sm' /> : (
                    <div style={{ fontSize: 12, marginTop: 6 }}>
                        {data.error ? <div style={{ color: '#c0392b' }}>{data.error}</div> : null}
                        {(data.members || []).length === 0 ? (
                            <div style={{ color: '#95a5a6' }}>No members yet — they appear once the group bridge is enabled (message activity / roster sync).</div>
                        ) : null}
                        <div style={{ maxHeight: 320, overflowY: 'auto' }}>
                            {(data.members || []).map((m) => (
                                <div key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '3px 0', borderBottom: '1px solid #f0f2f4' }}>
                                    <Badge color={m.kind === 'USER' ? 'success' : m.kind === 'REP' ? 'info' : 'secondary'}>
                                        {m.kind === 'USER' ? 'mapped' : m.kind === 'REP' ? 'Infurnia' : 'guest'}
                                    </Badge>
                                    <span style={{ flex: 1 }}>
                                        {m.displayName || '—'} <span style={{ color: '#95a5a6' }}>{mask_contact(m.waContactId)}</span>
                                        {(() => {
                                            const chip = member_link_chip(m);
                                            return chip ? (
                                                <span title={chip.title + (m.mappedAt ? ` · ${fmt_ts(m.mappedAt)}` : '')}
                                                    style={{ marginLeft: 6, fontSize: 9.5, color: chip.fg, background: chip.bg, border: '1px solid ' + chip.bd, borderRadius: 8, padding: '0 6px', fontWeight: 600, whiteSpace: 'nowrap' }}>
                                                    {chip.kind === 'self' ? <><FaIcon name='check' /> </> : ''}{chip.label}
                                                </span>
                                            ) : null;
                                        })()}
                                    </span>
                                    {can_unlink(m) ? (
                                        <Button size='sm' color='link' style={{ padding: 0, fontSize: 11, color: '#c0392b' }}
                                            title='Unlink this number from the user — it becomes a guest again'
                                            disabled={busy} onClick={() => unlink(m)}>unlink</Button>
                                    ) : m.kind === 'REP' ? (
                                        <Button size='sm' color='link' style={{ padding: 0, fontSize: 11, color: '#c0392b' }}
                                            disabled={busy} onClick={() => unmap_member(m)}>unmap</Button>
                                    ) : (
                                        <>
                                            <Input type='select' bsSize='sm' style={{ width: 180 }} value={picks[m.id] || ''}
                                                onChange={(e) => { const v = e.target.value; set_picks((pp) => ({ ...pp, [m.id]: v })); }}>
                                                <option value=''>— map to —</option>
                                                {(data.orgUsers || []).map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                                                <optgroup label='Infurnia team'>
                                                    {(data.reps || []).map((r) => <option key={r.userId} value={`rep:${r.userId}`}>{r.name}</option>)}
                                                </optgroup>
                                            </Input>
                                            <Button size='sm' color='primary' outline style={{ fontSize: 11, padding: '1px 8px' }}
                                                disabled={busy || !picks[m.id]} onClick={() => map_member(m)}>map</Button>
                                        </>
                                    )}
                                </div>
                            ))}
                        </div>
                        <div style={{ color: '#95a5a6', marginTop: 4 }}>
                            "Verified" numbers were linked by the user from the portal; "Mapped by rep" ones here. Both appear by name and count as org users; "Infurnia" members are our own team in the group; guests stay attributed to their number.
                        </div>
                    </div>
                )
            ) : null}
        </div>
    );
}

// ── Open-ticket cap (per org) ───────────────────────────────────────────────
// Client-raised tickets are capped per org: the platform default, or this
// org's override. The value rides on the client row of GET /comms/clients
// (ticketCapOverride); POST /clients/:storeId/ticket-cap { openPerOrg } sets
// it and null clears the override (ticket_triage.js).
function TicketCapPanel({ store_id }) {
    const [client, set_client] = useState(null);
    const [editing, set_editing] = useState(false);
    const [draft, set_draft] = useState('');
    const [busy, set_busy] = useState(false);
    const [err, set_err] = useState(null);
    const load = useCallback(async () => {
        try {
            const r = await cs_analysis_fetch({ path: '/api/v1/comms/clients' });
            set_client(client_for_store(r && r.clients, store_id) || {});
        } catch (e) { set_client({}); set_err((e && e.message) || 'unavailable'); }
    }, [store_id]);
    useEffect(() => { load(); }, [load]);
    const save = async () => {
        const v = parse_cap_input(draft);
        if (v === undefined) { set_err('Enter a whole number of open tickets, or leave it blank for the platform default.'); return; }
        set_busy(true);
        set_err(null);
        try {
            const r = await cs_analysis_fetch({ path: ticket_cap_path(store_id), method: 'POST', body: { openPerOrg: v } });
            // The response may carry the client (or its cap) — otherwise re-read.
            const next = r && typeof r === 'object' ? (r.client && typeof r.client === 'object' ? r.client : r) : null;
            if (next && Object.prototype.hasOwnProperty.call(next, 'ticketCapOverride')) set_client((c) => ({ ...(c || {}), ticketCapOverride: next.ticketCapOverride }));
            else await load();
            set_editing(false);
        } catch (e) { set_err((e && e.message) || 'could not save'); }
        set_busy(false);
    };
    return (
        <div style={{ marginTop: 12 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#2f6fed' }}>Ticket settings</div>
            <div style={{ fontSize: 12, marginTop: 4, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span>Open-ticket cap:</span>
                {client === null ? <Spinner size='sm' /> : editing ? (
                    <>
                        <Input bsSize='sm' type='number' min={0} step={1} value={draft} autoFocus placeholder='default'
                            onChange={(e) => set_draft(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') save(); if (e.key === 'Escape') set_editing(false); }}
                            style={{ width: 90, fontSize: 12 }} disabled={busy} />
                        <Button size='sm' color='primary' disabled={busy} onClick={save} style={{ fontSize: 11, padding: '1px 8px' }}>Save</Button>
                        <Button size='sm' color='link' disabled={busy} onClick={() => set_editing(false)} style={{ fontSize: 11, padding: 0 }}>cancel</Button>
                    </>
                ) : (
                    <>
                        <b>{ticket_cap_label(client)}</b>
                        <Button size='sm' color='link' style={{ padding: 0, fontSize: 11 }}
                            onClick={() => { const v = ticket_cap_of(client); set_draft(v == null ? '' : String(v)); set_editing(true); set_err(null); }}>edit</Button>
                    </>
                )}
            </div>
            <div style={{ color: '#95a5a6', fontSize: 11, marginTop: 2 }}>
                How many client-raised tickets this org can have open at once; blank restores the platform default.
            </div>
            {err ? <div style={{ color: '#c0392b', fontSize: 11, marginTop: 2 }}>{err}</div> : null}
        </div>
    );
}

// ── Tab root ────────────────────────────────────────────────────────────────
export default function ClientComms({ store_id, user_details, mode }) {
    const [groups, set_groups] = useState(null);
    const [bridge_candidates, set_bridge_candidates] = useState([]);
    const [selected_group, set_selected_group] = useState('general');
    const [tickets, set_tickets] = useState(null);
    const [status_filter, set_status_filter] = useState('active'); // active | closed | all
    const [selected, set_selected] = useState(null); // {ticket, channelId}
    const [show_create, set_show_create] = useState(false);
    const [ticket_prefill, set_ticket_prefill] = useState(null);
    const [error, set_error] = useState(null);

    const load = useCallback(async () => {
        try {
            const [gr, tk] = await Promise.all([
                cs_analysis_fetch({ path: `/api/v1/comms/clients/${store_id}/groups` }),
                cs_analysis_fetch({ path: '/api/v1/tickets', query: { storeId: store_id } }),
            ]);
            set_groups((gr && gr.groups) || []);
            set_bridge_candidates((gr && gr.candidates) || []);
            set_tickets((tk && tk.tickets) || []);
            set_error(null);
        } catch (e) {
            // COMMS not enabled / backend older than this UI — degrade gracefully.
            set_error((e && e.message) || 'comms unavailable');
            set_groups([]);
            set_tickets([]);
        }
    }, [store_id]);

    useEffect(() => { load(); }, [load]);
    useEffect(() => { const t = setInterval(load, 60000); return () => clearInterval(t); }, [load]);

    // keep the drawer's ticket fresh after actions
    const refresh_selected = useCallback(async () => {
        await load();
        if (selected) {
            try {
                const r = await cs_analysis_fetch({ path: `/api/v1/tickets/${selected.ticket.id}` });
                set_selected({ ticket: r.ticket, channelId: r.channelId });
            } catch { set_selected(null); }
        }
    }, [load, selected]);

    const visible = (tickets || []).filter((t) => {
        if (status_filter === 'active') return !['CLOSED', 'DECLINED'].includes(t.status);
        if (status_filter === 'closed') return ['CLOSED', 'DECLINED'].includes(t.status);
        return true;
    });

    return (
        <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
            {mode === 'tickets' ? null : (
            <div style={{ flex: '0 0 40%', minWidth: 320 }}>
                <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 6 }}>
                    Client groups
                    <span style={{ fontSize: 11, color: '#95a5a6', fontWeight: 400, marginLeft: 8 }}>
                        client-visible · ticket milestones post by module
                    </span>
                </div>
                {error ? <div style={{ fontSize: 12, color: '#c0392b' }}>Comms unavailable: {error}</div> : null}
                {groups === null && !error ? <Spinner size='sm' /> : null}
                {groups && groups.length ? (
                    <>
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 6 }}>
                            {groups.map((g) => (
                                <Button key={g.id} size='sm'
                                    color={selected_group === g.groupKey ? 'primary' : 'secondary'}
                                    outline={selected_group !== g.groupKey}
                                    style={{ padding: '2px 10px', fontSize: 11 }}
                                    onClick={() => set_selected_group(g.groupKey)}>
                                    {title_case(g.groupKey)}{g.waGroupId ? <> <FaIcon name='exchange-alt' label='bridged to WhatsApp' /></> : ''}
                                </Button>
                            ))}
                        </div>
                        {(() => {
                            const g = groups.find((x) => x.groupKey === selected_group) || groups[0];
                            return g ? (
                                <div style={{ padding: '18px 12px', background: '#fafbfc', borderRadius: 8, border: '1px dashed #d5dbe1', fontSize: 13 }}>
                                        {notify_mode(g.notify) ? (
                                            // Their group + explicits (Nikhil 2026-09-13): the group row's notify
                                            // says owner / following / muted for the signed-in rep; older backends
                                            // send none and this line stays hidden.
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, fontSize: 12 }}>
                                                <span style={{ color: '#7f8c8d' }}>Notifications for {title_case(g.groupKey)}:</span>
                                                <NotifyToggle channel_id={g.id} notify={g.notify}
                                                    on_change={(notify) => set_groups((gs) => (gs ? gs.map((x) => (x.id === g.id ? { ...x, notify } : x)) : gs))} />
                                            </div>
                                        ) : null}
                                        Conversations now live in the <a href='/home/chat' style={{ fontWeight: 600 }}>Chat inbox</a> — one place for every org, with search, history and media.
                                        This tab keeps the org's management tools: WhatsApp bridge, member mapping and tickets.
                                    </div>
                            ) : null;
                        })()}
                    </>
                ) : null}
                <BridgePanel store_id={store_id} groups={groups} candidates={bridge_candidates} on_changed={load} />
                <WaMembers store_id={store_id} />
                <TicketCapPanel store_id={store_id} />
            </div>
            )}
            {mode === 'comms' ? null : (

            <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                    <div style={{ fontWeight: 600, fontSize: 14 }}>Client Tickets {tickets ? `(${visible.length})` : ''}</div>
                    <div className='btn-group' role='group' style={{ boxShadow: 'none' }}>
                        {['active', 'closed', 'all'].map((f) => (
                            <Button key={f} size='sm' color={status_filter === f ? 'primary' : 'secondary'}
                                outline={status_filter !== f} style={{ padding: '3px 14px', fontSize: 12, fontWeight: 600, margin: 0 }}
                                onClick={() => set_status_filter(f)}>{f.charAt(0).toUpperCase() + f.slice(1)}</Button>
                        ))}
                    </div>
                    <Button size='sm' color='primary' style={{ marginLeft: 'auto' }} onClick={() => set_show_create(true)}>+ New Ticket</Button>
                </div>
                {tickets === null ? <Spinner size='sm' /> : visible.length === 0 ? (
                    <div style={{ fontSize: 12, color: '#95a5a6' }}>No {status_filter !== 'all' ? status_filter : ''} client tickets.</div>
                ) : (
                    <table className='table table-sm' style={{ fontSize: 13 }}>
                        <thead><tr style={{ fontSize: 11, color: '#7f8c8d' }}>
                            <th>Ref</th><th>Title</th><th>Module</th><th>Category</th><th>Status</th><th>Raised by</th><th>Updated</th>
                        </tr></thead>
                        <tbody>
                            {visible.map((t) => (
                                <tr key={t.id} style={{ cursor: 'pointer' }} onClick={() => set_selected({ ticket: t, channelId: t.channelId })}>
                                    <td style={{ whiteSpace: 'nowrap' }}>CT-{t.ref}</td>
                                    <td>
                                        {is_raiser_private(t) ? <FaIcon name='lock' label='private to raiser' title={PRIVATE_LOCK_TITLE} style={{ marginRight: 5, fontSize: 12 }} /> : null}
                                        {t.title}{t.clientVisible === false ? <Badge color='dark' style={{ marginLeft: 6 }}>internal</Badge> : null}
                                        {is_pending_triage(t) ? <span title='Client-raised — open it to accept, merge or decline' style={{ marginLeft: 6, fontSize: 10.5, color: '#8a6d1d', background: '#fff8e6', border: '1px solid #f0d48a', borderRadius: 10, padding: '1px 7px', fontWeight: 600, whiteSpace: 'nowrap' }}><FaIcon name='balance-scale' /> Needs triage</span> : null}
                                        {is_merged(t) ? <span style={{ marginLeft: 6, fontSize: 10.5, color: '#6c4bb5', background: '#f3efff', border: '1px solid #ddd2f6', borderRadius: 10, padding: '1px 7px', fontWeight: 600, whiteSpace: 'nowrap' }}><FaIcon name='arrow-right' /> {merged_into_label(t)}</span> : null}
                                    </td>
                                    <td>{label(t.module)}</td>
                                    <td>{label(t.category)}</td>
                                    <td><Badge style={{ backgroundColor: STATUS_COLORS[t.status] || '#95a5a6', color: '#fff' }}>{label(t.status)}</Badge></td>
                                    <td style={{ fontSize: 12 }}>{t.raisedByName || '—'}{t.raisedByType === 'CLIENT_USER' ? ' (client)' : ''}</td>
                                    <td style={{ fontSize: 12, whiteSpace: 'nowrap' }}>{fmt_ts(t.updatedAt)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
            )}
            <CreateTicketModal store_id={store_id} open={show_create}
                on_close={() => set_show_create(false)}
                on_created={() => { set_show_create(false); load(); }} upload_channel_id={(((groups || []).find((g) => g.groupKey === 'general')) || (groups || [])[0] || {}).id} initial={ticket_prefill} />
            {selected ? (
                <TicketDrawer ticket={selected.ticket} channel_id={selected.channelId} store_id={store_id}
                    on_close={() => set_selected(null)} on_changed={refresh_selected} />
            ) : null}
        </div>
    );
}
